/**
 * Der Unterpfad, unter dem die Anwendung ausgeliefert wird.
 *
 * Normalerweise ist er leer: Die Anwendung liegt an der Wurzel eines Namens. Steht
 * sie hinter einem Proxy in einem Unterpfad — `https://werkzeuge.intern/devtools/`,
 * weil an der Wurzel schon etwas anderes liegt —, dann muss sie das **beim Bauen**
 * wissen und nicht erst zur Laufzeit: Vite schreibt die Adressen der gebündelten
 * Dateien in die `index.html`, und die stehen darin absolut. Deshalb kommt der Wert
 * aus `import.meta.env.BASE_URL`, also aus `base` in `vite.config.ts`, gesetzt über
 * `DEVTOOLS_BASE_PATH` (ADR-0014).
 *
 * Zwei Richtungen sind zu unterscheiden:
 *
 *   * **Hinaus** — was die Anwendung an den Browser gibt: Adressen für die
 *     Chronik und für `fetch`. Sie tragen den Unterpfad (`withBase`).
 *   * **Herein** — was aus der Adresszeile gelesen wird. Dort steht der Unterpfad
 *     mit drin und muss weg, bevor der Ort erkannt wird (`withoutBase`).
 *
 * Der Server sieht ihn nie: Der Proxy schneidet ihn ab, bevor er weiterleitet.
 * Genau deshalb bleibt PHP von dieser Betriebsart unberührt.
 */

/** Ohne abschließenden, mit führendem Schrägstrich — oder leer. */
export function baseOf(raw: string | undefined): string {
  if (raw === undefined) {
    return "";
  }

  const trimmed = raw.replace(/\/+$/, "");

  if (trimmed === "") {
    return "";
  }

  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

export const BASE = baseOf(import.meta.env.BASE_URL);

/** Eine anwendungsinterne Adresse, wie der Browser sie sehen soll. */
export function withBase(path: string, base: string = BASE): string {
  return base === "" ? path : `${base}${path}`;
}

/**
 * Der Pfad ohne den Unterpfad. Was nicht darunter liegt, bleibt unverändert —
 * eine getippte Adresse führt in die Liste und nie in eine Fehlerseite.
 */
export function withoutBase(path: string, base: string = BASE): string {
  if (base === "" || path === base) {
    return base === "" ? path : "/";
  }

  return path.startsWith(`${base}/`) ? path.slice(base.length) : path;
}
